/**
 * Certification reimbursement applications — a domain of their own.
 *
 * ── Why this is not an Expense ────────────────────────────────────────────────────────────
 *
 * It was, briefly: a category on the shared Expenses table with a link to the certification.
 * That worked and it coupled two things that should be independently maintainable — every change
 * to the certification flow had to be safe for travel claims and meal claims, and the shared
 * expense model grew columns nine categories never use.
 *
 * So a certification claim is now its own record in its own table, with its own identity and its
 * own approval fields. Nothing in the Expenses module knows it exists.
 *
 * ── Two entities, related but not the same ────────────────────────────────────────────────
 *
 *   Certification                 "has this employee completed this qualification?"
 *   CertificationReimbursement    "is this employee asking to be paid for it?"
 *
 * A certification can exist with no claim — one somebody earned before joining, recorded on All
 * Certifications. A claim always points at a certification, by `certificationId`. Keeping them
 * apart is what lets the certification outlive the claim and keep its own attempt history.
 *
 * ── Result is not a decision ──────────────────────────────────────────────────────────────
 *
 * `result` (Passed/Failed) says what happened in the exam. `managerApprovalStatus` and
 * `adminApprovalStatus` say what the approvers decided about paying. A failed exam can be
 * approved; a passed one can be declined. The two never share a field, and REJECTED never means
 * "failed the exam".
 */

import { CertificationResult, calculateRetakeDeadline } from './certification';

/** One approver's verdict. Deliberately not reusing an expense enum — different domain. */
export enum ReimbursementApprovalStatus {
  PENDING = 'Pending',
  APPROVED = 'Approved',
  REJECTED = 'Rejected',
}

/**
 * Where the whole application stands, derived from the two approvals.
 *
 * Stored as well as derivable, because it is what the list is filtered and sorted by and a query
 * cannot compute it. `deriveOverallStatus` is the single place it is worked out, so the stored
 * value can never be reached by two different routes.
 */
export enum ReimbursementStatus {
  PENDING_MANAGER = 'Pending Manager',
  PENDING_ADMIN = 'Pending Admin',
  PENDING_FINANCE = 'Pending Finance',
  /**
   * Every approver said yes and the money has been paid. This is the END of the workflow.
   *
   * There is deliberately no state between "finance approved" and "reimbursed": finance's
   * approval IS the act of paying, recorded by one action. A separate approved-but-unpaid state
   * would be a queue nobody owns.
   */
  REIMBURSED = 'Reimbursed',
  REJECTED = 'Rejected',
}

export interface CertificationReimbursement {
  id: string;
  /** The certification this claim is for. The only link between the two entities. */
  certificationId: string;

  employeeEmail: string;
  employeeName?: string;
  employeeId?: string;
  /** The reporting manager this was routed to, captured at submission. */
  managerEmail?: string;

  /*
   * Denormalised from the certification, on purpose.
   *
   * An approver's queue shows dozens of claims and needs the certification's name, provider,
   * dates and result on every row. Reading the certification for each would be a query per row;
   * copying the handful of fields that are decided at submission and never change afterwards
   * costs nothing and keeps the queue a single read. The certification remains the source of
   * truth for anything that CAN change — the attempt history, and the result after a retake.
   */
  certificationName: string;
  provider: string;
  examDate?: string;
  resultDate: string;
  result: CertificationResult;
  retakeDeadline?: string;

  reimbursementAmount: number;
  /** ISO 4217. The application is single-currency today; the field stops that being implicit. */
  currency: string;
  /** S3 key of the certificate or failed-result document backing THIS claim. */
  documentKey?: string;
  documentName?: string;
  notes?: string;

  managerApprovalStatus: ReimbursementApprovalStatus;
  managerApprovedBy?: string;
  managerApprovedAt?: string;

  adminApprovalStatus: ReimbursementApprovalStatus;
  adminApprovedBy?: string;
  adminApprovedAt?: string;

  /*
   * The third and final stage. Optional on the type because rows written before finance existed
   * carry nothing here — `financeStatusOf` reads an absent value as PENDING, so an old claim
   * lands in the finance queue rather than reading as approved-and-paid.
   */
  financeApprovalStatus?: ReimbursementApprovalStatus;
  financeApprovedBy?: string;
  financeApprovedAt?: string;

  /** Why it was declined, whichever approver declined it. */
  rejectionReason?: string;

  status: ReimbursementStatus;
  createdAt: string;
  updatedAt: string;
}

/**
 * The overall status implied by the two approvals.
 *
 * One function, used when writing the record and when reading a stored one back, so a row whose
 * `status` disagrees with its own approval fields cannot be produced by any path.
 *
 * Order matters: a rejection ends the application whoever made it, and is checked before the
 * pending states. Without that, a manager-rejected claim whose admin field is still PENDING
 * would read as "Pending Admin" and sit in a queue nobody should ever action.
 */
export function deriveOverallStatus(
  managerStatus: ReimbursementApprovalStatus,
  adminStatus: ReimbursementApprovalStatus,
  financeStatus: ReimbursementApprovalStatus = ReimbursementApprovalStatus.PENDING
): ReimbursementStatus {
  if (
    managerStatus === ReimbursementApprovalStatus.REJECTED ||
    adminStatus === ReimbursementApprovalStatus.REJECTED ||
    financeStatus === ReimbursementApprovalStatus.REJECTED
  ) {
    return ReimbursementStatus.REJECTED;
  }
  if (managerStatus !== ReimbursementApprovalStatus.APPROVED) {
    return ReimbursementStatus.PENDING_MANAGER;
  }
  if (adminStatus !== ReimbursementApprovalStatus.APPROVED) {
    return ReimbursementStatus.PENDING_ADMIN;
  }
  if (financeStatus !== ReimbursementApprovalStatus.APPROVED) {
    return ReimbursementStatus.PENDING_FINANCE;
  }
  return ReimbursementStatus.REIMBURSED;
}

/**
 * A claim's finance verdict, with an absent one read as PENDING.
 *
 * Every reader goes through this rather than touching the field. Claims written before the
 * finance stage existed have no value there, and `undefined !== APPROVED` would be true by
 * accident in some comparisons and false in others; one accessor makes the default explicit.
 */
export function financeStatusOf(
  claim: Pick<CertificationReimbursement, 'financeApprovalStatus'>
): ReimbursementApprovalStatus {
  return claim.financeApprovalStatus || ReimbursementApprovalStatus.PENDING;
}

/** What a claim form was filled in with, before it becomes a record. */
export interface ReimbursementDraft {
  certificationId: string;
  certificationName: string;
  provider: string;
  examDate?: string;
  resultDate: string;
  result: CertificationResult;
  reimbursementAmount: number;
  currency?: string;
  documentKey?: string;
  documentName?: string;
  notes?: string;
}

/**
 * What is wrong with a claim, as a list of sentences.
 *
 * Only the reimbursement's own rules. The certification half is validated by
 * validateCertificationDraft, which is shared with All Certifications so the mandatory-document
 * rule cannot be looser on one form than the other.
 */
export function validateReimbursementDraft(draft: Partial<ReimbursementDraft>): string[] {
  const errors: string[] = [];

  if (!String(draft.certificationId || '').trim()) {
    // Without it the claim points at nothing and an approver has no evidence to decide on.
    errors.push('The claim is not linked to a certification.');
  }
  const amount = Number(draft.reimbursementAmount);
  if (!Number.isFinite(amount) || amount <= 0) {
    errors.push('Enter the amount you are claiming.');
  }
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(draft.resultDate || ''))) {
    errors.push('Result date is required.');
  }
  if (!String(draft.documentKey || '').trim()) {
    errors.push(
      draft.result === CertificationResult.FAILED
        ? 'Upload the result document. It is required for a failed attempt as well as a pass.'
        : 'Upload the certificate.'
    );
  }

  return errors;
}

/** The retake deadline a FAILED claim carries. Passed claims have none. */
export function reimbursementRetakeDeadline(
  result: CertificationResult,
  resultDate: string
): string | undefined {
  return result === CertificationResult.FAILED ? calculateRetakeDeadline(resultDate) : undefined;
}
